import type { Scene, SceneElement } from './types.js';

/**
 * Which layer folders one Spotify answer is allowed to fill.
 *
 * Customily's player is a folder (`SpotifyPlayer: true`) holding the album
 * cover, the scan code, the track and artist lines and the transport artwork.
 * A design may carry two of them — "his song" and "her song" on one plaque —
 * and a pick made against one folder must not rewrite the other's lines, which
 * is exactly what happens when the fill walks every role-marked layer in the
 * scene.
 *
 * `undefined` means "no folder said it was a player", and the caller falls back
 * to the whole scene: plaques built before folders carried the flag, and every
 * imported one until `repair-templates` has run, have no group to scope to and
 * must keep working.
 *
 * Nested folders count as inside the player: the editor lets a designer file
 * the two text lines into a sub-folder, and their role does not stop applying
 * because of it.
 */
export function spotifyPlayerScope(
  scene: Pick<Scene, 'groups'>,
  anchor: SceneElement | undefined,
): Set<string> | undefined {
  const groups = scene.groups ?? [];
  // Climb from the anchor's own folder to the nearest ancestor flagged as a
  // player, so a line filed into a sub-folder still belongs to its player.
  let root = anchor?.groupId ? groups.find((g) => g.id === anchor.groupId) : undefined;
  const seen = new Set<string>();
  while (root && !root.spotifyPlayer && root.parentId && !seen.has(root.id)) {
    seen.add(root.id);
    root = groups.find((g) => g.id === root!.parentId);
  }
  if (!root?.spotifyPlayer) return undefined;
  const ids = new Set([root.id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const g of groups) {
      if (g.parentId && ids.has(g.parentId) && !ids.has(g.id)) {
        ids.add(g.id);
        grew = true;
      }
    }
  }
  return ids;
}

/** Whether a layer falls inside the scope a pick may fill. */
export function inSpotifyScope(
  scope: Set<string> | undefined,
  element: SceneElement,
): boolean {
  if (!scope) return true;
  return Boolean(element.groupId && scope.has(element.groupId));
}
